import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Users,
  Target,
  ShieldCheck,
  CheckCircle2,
  FileSpreadsheet,
  Layers,
  ChevronRight,
  X,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  PROFILING_HERO_TAGS,
  MAIN_ADVANTAGES,
  DOSSIER_SECTIONS,
  EXPERIENCE_PILLARS,
} from "../../Data/companyProfilingData";

import "../../Style/services/data-validation.css";
import "../../Style/services/b2b-appointment.css";
import "../../Style/services/company-profiling.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -5, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function CompanyProfiling() {
  const [activeSection, setActiveSection] = useState(0);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePillClick = (idx) => {
    setActiveSection(idx);
    if (window.innerWidth <= 1024) {
      setIsMobileModalOpen(true);
    }
  };

  const currentModule = DOSSIER_SECTIONS[activeSection];
  const CurrentIcon = currentModule.icon;

  return (
    <div className="validation-page profiling-page">
      <Seo
        title="B2B Company Profiling Services | Conectify Tech"
        description="Better business opportunities through deep company profiling. Uncover enterprise decision-making hierarchies, vendor selection criteria, and 12-point corporate dossiers."
      />

      {/* 1. Hero Section */}
      <section className="val-hero profiling-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Deep Commercial Intelligence</span>
            </div>

            <h1 className="val-hero__title">
              Better Business Opportunities Through{" "}
              <span className="text-gradient">Deep Company Profiles</span>
            </h1>

            <p className="val-hero__lead">
              Obviously, the more you know about your prospects, the better your chances of
              converting them into long-term customers. Thorough company profiling is about going
              straight into a company to really understand its decision-making processes, including
              who is involved, the criteria for choosing a supplier, and what is expected of a supplier.
            </p>

            <p className="val-hero__sublead">
              Conectify Tech helps you truly understand your best prospects so you can deploy the
              most effective sales and marketing measures.
            </p>

            <div className="val-hero__tags">
              <span className="val-tag">
                <Layers size={13} className="text-primary" />
                <span>Multi-Tier Dossier Architecture</span>
              </span>
              {PROFILING_HERO_TAGS.map((tag) => {
                const Icon = tag.icon;
                return (
                  <span key={tag.label} className="val-tag">
                    <Icon size={13} className="text-primary" />
                    <span>{tag.label}</span>
                  </span>
                );
              })}
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Request Sample Profile
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                View Dossier Framework
              </Button>
            </div>
          </motion.div>

          {/* Interactive Company Dossier HUD */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="profiling-hud-card">
              <div className="profiling-hud-card__top">
                <div className="flex items-center gap-2">
                  <Building2 size={18} className="text-primary" />
                  <span className="font-bold text-sm">Account Dossier Telemetry</span>
                </div>
                <span className="hud-live-tag">
                  <span className="live-dot"></span> Live Intelligence
                </span>
              </div>

              <div className="profiling-sample-matrix">
                <div className="profile-identity">
                  <div className="profile-avatar">
                    <Building2 size={24} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4>Global Enterprise Core Inc.</h4>
                      <span className="badge-tier">Tier 1 Target</span>
                    </div>
                    <p>Cloud Security & Infrastructure • 4,200+ Employees</p>
                  </div>
                </div>

                <div className="profile-metrics-strip">
                  <div className="metric">
                    <span className="lbl">Decision Matrix</span>
                    <span className="val text-primary">C-Suite & Board</span>
                  </div>
                  <div className="metric">
                    <span className="lbl">Supplier Criteria</span>
                    <span className="val text-success">SOC2 & Multi-Cloud</span>
                  </div>
                  <div className="metric">
                    <span className="lbl">Annual Capex</span>
                    <span className="val">$14.2M</span>
                  </div>
                </div>

                <div className="profile-decision-chain">
                  <span className="chain-label">Primary Stakeholders Identified:</span>
                  <div className="chain-chips">
                    <span className="chip">
                      <Users size={12} /> Chief Information Security Officer
                    </span>
                    <span className="chip">
                      <Target size={12} /> Head of Global Procurement
                    </span>
                  </div>
                </div>
              </div>

              <div className="profiling-hud-card__footer">
                <CheckCircle2 size={14} className="text-success" />
                <span>Primary & Secondary Verified Intelligence Guarantee</span>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* 2. Main Advantages Section */}
      <section className="profiling-advantages-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Measurable Sales Impact</span>
            <h2 className="section-title">Main Advantages</h2>
            <p className="section-desc max-w-2xl mx-auto">
              The detailed information that we provide on your key prospects enables your team to
              execute high-velocity, customized account penetration:
            </p>
          </div>

          <div className="profiling-advantages-grid">
            {MAIN_ADVANTAGES.map((adv) => {
              const Icon = adv.icon;
              return (
                <motion.div
                  key={adv.title}
                  className="val-why-card profiling-adv-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{adv.title}</h4>
                  <p>{adv.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 3. The Profiling Process & 12 Dossier Sections Blueprint */}
      <section className="profiling-process-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Rigorous Research Methodology</span>
            <h2 className="section-title">The Profiling Process</h2>
            <p className="section-desc max-w-3xl mx-auto">
              Using a combination of primary and secondary desk research skills, our highly
              effective and accomplished researchers search for existing data and interview key
              personnel at all levels of selected companies using a combination of market research
              and telemarketing techniques.
            </p>
          </div>

          {/* Interactive 12-Section Dossier Inspector */}
          <div className="profiling-dossier-shell">
            {/* Left: 12-Item Blueprint Grid */}
            <div className="profiling-dossier-menu">
              <div className="dossier-menu-header">
                <FileSpreadsheet size={16} className="text-primary" />
                <span>Standard Company Profile Blueprint (12 Deliverable Modules)</span>
              </div>
              <div className="dossier-items-grid">
                {DOSSIER_SECTIONS.map((sec, idx) => {
                  const Icon = sec.icon;
                  const isSelected = activeSection === idx;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      className={`dossier-pill ${isSelected ? "is-active" : ""}`}
                      onClick={() => handlePillClick(idx)}
                    >
                      <div className="dossier-pill__left">
                        <span className="dossier-pill__num">{sec.id}</span>
                        <Icon size={15} className="dossier-pill__icon" />
                        <span className="dossier-pill__name">{sec.title}</span>
                      </div>
                      <ChevronRight size={14} className="dossier-pill__arrow" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Desktop Live Inspector Card */}
            <div className="profiling-dossier-display desktop-only-display">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentModule.id}
                  className="dossier-inspect-screen"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="dossier-inspect-screen__top">
                    <span className="category-tag">{currentModule.category}</span>
                    <span className="module-idx">Section {currentModule.id} of 12</span>
                  </div>

                  <div className="dossier-inspect-screen__main">
                    <div className="inspect-icon">
                      <CurrentIcon size={26} />
                    </div>
                    <h3>{currentModule.title}</h3>
                    <p>{currentModule.desc}</p>
                  </div>

                  <div className="dossier-inspect-screen__telemetry">
                    <div className="telemetry-item">
                      <span className="lbl">Data Sourcing</span>
                      <span className="val">Primary Interviews & SEC Filings</span>
                    </div>
                    <div className="telemetry-item">
                      <span className="lbl">Validation</span>
                      <span className="val text-success">Human-Audited</span>
                    </div>
                    <div className="telemetry-item">
                      <span className="lbl">Format</span>
                      <span className="val">Executive PDF / CRM Feed</span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Container>
      </section>

      {/* Mobile / Small Screen Modal Popup */}
      <AnimatePresence>
        {isMobileModalOpen && (
          <div className="dossier-modal-backdrop" onClick={() => setIsMobileModalOpen(false)}>
            <motion.div
              className="dossier-modal-card"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="dossier-modal-close"
                onClick={() => setIsMobileModalOpen(false)}
                aria-label="Close details"
              >
                <X size={18} />
              </button>

              <div className="dossier-inspect-screen__top">
                <span className="category-tag">{currentModule.category}</span>
                <span className="module-idx">Section {currentModule.id} of 12</span>
              </div>

              <div className="dossier-inspect-screen__main mt-2">
                <div className="inspect-icon">
                  <CurrentIcon size={26} />
                </div>
                <h3>{currentModule.title}</h3>
                <p>{currentModule.desc}</p>
              </div>

              <div className="dossier-inspect-screen__telemetry">
                <div className="telemetry-item">
                  <span className="lbl">Data Sourcing</span>
                  <span className="val">Primary Interviews & SEC Filings</span>
                </div>
                <div className="telemetry-item">
                  <span className="lbl">Validation</span>
                  <span className="val text-success">Human-Audited</span>
                </div>
                <div className="telemetry-item">
                  <span className="lbl">Format</span>
                  <span className="val">Executive PDF / CRM Feed</span>
                </div>
              </div>

              <Button
                size="sm"
                className="w-full mt-5 justify-center"
                onClick={() => setIsMobileModalOpen(false)}
              >
                Done
              </Button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 4. In-Depth Knowledge and Experience in Business Advantage */}
      <section className="profiling-experience-section">
        <Container>
          <div className="appt-split-box">
            <div className="appt-split-box__content">
              <div className="section-eyebrow">Enterprise Advantage</div>
              <h2>In-Depth Knowledge and Experience</h2>
              <p className="lead-text">
                We combine industry-tested knowledge with frontline field experience to craft
                uncompromising, high-impact intelligence on your best enterprise targets.
              </p>

              <div className="profiling-experience-list">
                {EXPERIENCE_PILLARS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div key={p.title} className="profiling-exp-item">
                      <div className="profiling-exp-item__icon">
                        <Icon size={18} />
                      </div>
                      <div className="profiling-exp-item__text">
                        <h4>{p.title}</h4>
                        <p>{p.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="profiling-visual-stack">
              <div className="cadence-orbit">
                <div className="cadence-orbit__ring"></div>
                <div className="cadence-center-node">
                  <ShieldCheck size={30} className="text-primary" />
                  <span>Verified Dossier</span>
                </div>

                <div className="orbit-node orbit-node--top">
                  <span>Desk Research</span>
                </div>
                <div className="orbit-node orbit-node--right">
                  <span>Tele-Interviews</span>
                </div>
                <div className="orbit-node orbit-node--bottom">
                  <span>Vendor Criteria</span>
                </div>
                <div className="orbit-node orbit-node--left">
                  <span>Market Reports</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 5. Bottom CTA */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Accelerate Your Strategic Account Sales with Company Profiles</h2>
            <p>
              Equip your enterprise sales team with comprehensive corporate dossiers, complete
              decision-maker hierarchies, and custom vendor selection criteria.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Commission a Custom Company Profile
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}